import http from "@services/httpService"
import { PagedResultDto } from "@services/dto/pagedResultDto"
import { L, LNotification } from "@lib/abpUtility"
import { notifyError, notifySuccess } from "@lib/helper"
import { downloadFile } from "@lib/helperFile"
import { ExportFileTypeEnum, ModuleEnum } from "@lib/enum"
import { ExportContractInquiryPaymentTermDto } from "@models/export/exportModel"
import type { IContractInquiryUpdateInput } from "@models/contract/contractInquiryUpdateModel"
import type { IPinkBookVatReceiptLine } from "@models/contract/pinkBookVatReceiptLineModel"
import type { IPaymentSlipReceipt } from "@models/contract/paymentSlipReceiptModel"
import type {
  IContractDebtSpreadItem,
  IContractDebtSpreadParams,
  IContractRefundSpreadItem,
  IContractRefundSpreadParams,
} from "@models/contract/contractBalanceModel"
import dayjs from "dayjs"

/**
 * Overview 7 chỉ số tổng hợp lịch thu tiền hợp đồng — BE tính sẵn
 * (endpoint `ContractInquiry/GetContractPaymentScheduleOverview`).
 * `remaining` = debt − collected − earlyDiscount + liquidationValue.
 */
export interface IContractPaymentScheduleOverview {
  debt: number
  collected: number
  earlyDiscount: number
  liquidationValue: number
  remaining: number
  interest: number
  otherNet: number
}

/**
 * Lãi CÒN PHẢI TRẢ của một hợp đồng tại `calculateDate`
 * (endpoint `ContractInquiry/GetRemainingInterestAmountByContract`).
 * `interestAmount` = lãi phát sinh − lãi đã thu − lãi được miễn/giảm; CÓ THỂ ÂM.
 * Đừng nhầm với `interest` của `IContractPaymentScheduleOverview` — đó là lãi ĐÃ THU.
 */
export interface IContractInterestPayable {
  contractId: number
  projectId: number
  calculateDate: string
  interestAmount: number
}

class ContractInquiryService {
  public async create(body: any) {
    const res = await http.post("api/services/app/ContractInquiry/Create", body)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  /** Cập nhật phiếu thu (header + journal lines) — chỉ khi chưa đồng bộ */
  public async update(body: IContractInquiryUpdateInput) {
    const res = await http.put("api/services/app/ContractInquiry/Update", body)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  /**
   * Feature-0378 — xóa mềm MỘT phiếu thu + trả lại đúng phần công nợ phiếu đó đã ghi.
   * Permission: `PagesAdministration.ContractInquiry.Delete`.
   *
   * BE chỉ cho xóa khi CẢ HAI trạng thái đồng bộ = `SyncStatus.NONE` (1) — `FAIL` (4) KHÔNG
   * xóa được (phiếu FAIL đã dựng dòng Invoice/AccountingIntegration không có cờ soft-delete).
   * `deleteReason` BẮT BUỘC (1–2000 ký tự), BE ghi vào cột `ContractInquiry.DeleteReason`.
   *
   * Một lần lập phiếu có thể sinh nhiều phiếu cùng `transactionCode` ⇒ xóa lẻ để lại lô không
   * trọn vẹn; mặc định nên dùng `deleteByTransactionCode`.
   *
   * BE trả `Task` (không có `result`) nên hàm này không trả gì.
   */
  public async delete(id: number, deleteReason: string): Promise<void> {
    await http.delete("api/services/app/ContractInquiry/Delete", {
      params: { id, deleteReason },
    })
    notifySuccess(LNotification("SUCCESS"), LNotification("DELETE_SUCCESSFULLY"))
  }

  /**
   * Feature-0378 — xóa mềm TOÀN BỘ phiếu của một lần ghi nhận (cùng `transactionCode`) và trả
   * lại công nợ cả lô. Permission: `PagesAdministration.ContractInquiry.Delete`.
   *
   * BE validate cả tập trước khi ghi: chỉ cần MỘT phiếu trong lô đã đồng bộ / là phiếu chuyển
   * cọc / còn chứng từ ngoài lô trỏ vào là chặn cả lô — không có ca xóa nửa lô.
   * `deleteReason` được ghi vào từng phiếu trong lô.
   */
  public async deleteByTransactionCode(
    transactionCode: string,
    deleteReason: string
  ): Promise<void> {
    await http.delete(
      "api/services/app/ContractInquiry/DeleteByTransactionCode",
      { params: { transactionCode, deleteReason } }
    )
    notifySuccess(LNotification("SUCCESS"), LNotification("DELETE_SUCCESSFULLY"))
  }

  public async calculationContractFeeList(body: any) {
    const res = await http.post(
      "api/services/app/ContractInquiry/CalculationContractFeeList",
      body
    )
    return res.data.result
  }

  public async getAllInquiryContract(
    params: any
  ): Promise<PagedResultDto<any>> {
    const res = await http.get(
      "api/services/app/ContractInquiry/GetAllInquiryContract",
      {
        params,
      }
    )
    return res.data.result
  }
  public async exportContractInquiry(params: any): Promise<any> {
    const res = await http.get("api/Export/ExportContractInquiry", {
      params,
      responseType: "blob",
    })
    downloadFile(res.data, `EXPORT-CONTRACT-INQUIRY-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`)
  }

  public async exportInvoiceIntegration(params: any): Promise<any> {
    const res = await http.get("api/Export/ExportContractInquiryInvoice", {
      params,
      responseType: "blob",
    })
    downloadFile(res.data, `EXPORT-INVOICE-INTEGRATION-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`)
  }

  public async exportAccountingIntegration(params: any): Promise<any> {
    const res = await http.get("api/Export/ExportContractInquiryAccounting", {
      params,
      responseType: "blob",
    })
    downloadFile(res.data, `EXPORT-ACCOUNTING-INTEGRATION-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`)
  }

  public async exportContractInquiryPaymentTerm(
    params: ExportContractInquiryPaymentTermDto
  ): Promise<any> {
    const res = await http.get("api/Export/ExportContractInquiryPaymentTerm", {
      params,
      responseType: "blob",
    })
    const extension = params.fileType === ExportFileTypeEnum.Pdf ? "pdf" : "xlsx"
    downloadFile(
      res.data,
      `EXPORT-CONTRACT-INQUIRY-PAYMENT-TERM-${dayjs().format(
        "DDMMYYYYHHmmss"
      )}.${extension}`
    )
  }

  public async getAllContractInquiry(
    params: any
  ): Promise<PagedResultDto<any>> {
    const res = await http.get(
      "api/services/app/ContractInquiry/GetAllContractInquiry",
      {
        params,
      }
    )
    return res.data.result
  }

  public async getAllContractInquiryBooking(
    params: any
  ): Promise<PagedResultDto<any>> {
    const res = await http.get(
      "api/services/app/ContractInquiry/GetAllContractInquiryBooking",
      {
        params,
      }
    )
    return res.data.result
  }

  public async getListContractComboBox(): Promise<any[]> {
    const res = await http.get(
      "api/services/app/Contract/GetListContractComboBox"
    )
    return res.data.result
  }
  // Lấy danh sách phiếu thu đã có của hợp đồng (cơ sở để tạo phiếu chi / điều chỉnh)
  public async getReceiptListByContract(contractId: number): Promise<any[]> {
    const res = await http.get(
      "api/services/app/ContractInquiry/GetReceiptListByContract",
      { params: { contractId } }
    )
    return res.data.result
  }

  //Phân bổ phiếu thu
  public async getPlanReceiptAllocation(params: any): Promise<any[]> {
    const res = await http.get(
      "/api/services/app/ContractInquiry/GetPlanReceiptAllocation",
      {
        params,
      }
    )
    return res.data.result
  }
  //Phân bổ phiếu hoàn tiền
  public async getReturnAllocation(params: any): Promise<any[]> {
    const res = await http.get(
      "/api/services/app/ContractInquiry/GetReturnAllocation",
      {
        params,
      }
    )
    return res.data.result
  }
  /**
   * Feature-0376 — danh sách phiếu thu làm NGUỒN cho phiếu chi thanh lý, số tiền đã net
   * với mọi điều chỉnh/chi trước đó (phiếu thu 10đ đã điều chỉnh −2đ ⇒ trả về 8đ).
   * Permission: `PagesAdministration.ContractInquiry.Read`.
   *
   * Server chỉ trả dòng thuộc 3 mã phí UNT/MNT/MGF và lọc ở MỨC DÒNG (một phiếu thu có thể
   * vừa có UNT vừa có LAT) — nhờ vậy tổng ở sự kiện thanh lý bằng đúng tổng chi tiết.
   *
   * Trả `[]` (KHÔNG throw) khi hợp đồng không có phiếu thu nào đủ điều kiện — màn lập phiếu
   * phải mở được với mọi hợp đồng.
   */
  public async getReceiptsForPaymentSlip(
    contractId: number
  ): Promise<IPaymentSlipReceipt[]> {
    const res = await http.get(
      "/api/services/app/ContractInquiry/GetReceiptsForPaymentSlip",
      {
        params: { contractId },
      }
    )
    return res.data.result ?? []
  }

  // Phân bổ phiếu chi từ các kỳ (period) có giá trị âm
  public async getPaymentSlipFromNegativePeriods(params: any): Promise<any[]> {
    const res = await http.get(
      "/api/services/app/ContractInquiry/GetPaymentSlipFromNegativePeriods",
      {
        params,
      }
    )
    return res.data.result
  }

  /**
   * Feature-0369 — 2 dòng đề xuất cho phiếu thu VAT sổ hồng.
   * Permission: `PagesAdministration.Contract.PaymentTermRead`.
   *
   * Trả về **0, 1 hoặc 2** dòng — phải xử lý cả ba, đừng giả định luôn có 2:
   * - `[]` : HĐ không có đợt tick cờ · `pinkBookVatAmount = 0` · HĐ/lịch thanh toán không tồn tại → ẩn chức năng
   * - 1 dòng : không xác định được đợt cấp sổ hồng (ràng buộc MỀM cố ý — phiếu vẫn tạo được)
   * - 2 dòng : dòng thu VAT + dòng ảo
   *
   * ⚠️ Feature-0370 bỏ tham số `objectType`: cấu hình tài khoản treo khoá theo dự án chứ không theo
   * loại phiếu, và server không còn đề xuất cặp tài khoản. Dòng ảo trả về có
   * `debitAccountId`/`creditAccountId = null` và `amount` DƯƠNG → kế toán tự chọn cặp tài khoản và tự
   * quyết dấu; server đánh cờ `isSuspenseLine` khi tạo phiếu nếu bộ 3 khớp cấu hình active.
   */
  public async getPinkBookVatReceiptLines(
    contractId: number
  ): Promise<IPinkBookVatReceiptLine[]> {
    const res = await http.get(
      "/api/services/app/ContractInquiry/GetPinkBookVatReceiptLines",
      {
        params: { contractId },
      }
    )
    return res.data.result
  }
  public async get(id: number): Promise<any> {
    if (!id) {
      notifyError(L("ERROR"), L("ENTITY_NOT_FOUND"))
    }
    const res = await http.get("api/services/app/ContractInquiry/Get", {
      params: { id },
    })
    const { result } = res.data
    return result
  }

  //

  public async getTotalPaidAmountByContractId(params: any): Promise<number> {
    const res = await http.get(
      "api/services/app/ContractInquiry/GetTotalPaidAmountByContractId",
      {
        params,
      }
    )
    return res.data.result
  }

  // Tổng tiền lãi đã thu của hợp đồng (khoản (v) phải trừ lại khi thanh lý)
  public async getTotalInterestReceiptAmountByContractId(
    contractId: number
  ): Promise<number> {
    const res = await http.get(
      "api/services/app/ContractInquiry/GetTotalInterestReceiptAmountByContractId",
      {
        params: { contractId },
      }
    )
    return res.data.result
  }

  // Tổng giá trị đã thanh toán của một booking (dùng làm mặc định số tiền hoàn)
  public async getTotalPaidAmountByBookingId(bookingId: number): Promise<number> {
    const res = await http.get(
      "api/services/app/ContractInquiry/GetTotalPaidAmountByBookingId",
      {
        params: { bookingId },
      }
    )
    return res.data.result
  }

  // contract payment schedule

  // Overview 7 chỉ số tổng hợp lịch thu tiền (BE tính sẵn) cho tab Tiến độ thanh toán
  public async getContractPaymentScheduleOverview(
    contractId: number
  ): Promise<IContractPaymentScheduleOverview> {
    if (!contractId) {
      notifyError(L("ERROR"), L("ENTITY_NOT_FOUND"))
    }
    const res = await http.get(
      "api/services/app/ContractInquiry/GetContractPaymentScheduleOverview",
      {
        params: { contractId },
      }
    )
    return res.data.result
  }

  /**
   * Lãi còn phải trả của 1 hợp đồng tại 1 mốc ngày.
   * `calculateDate` là ISO string và BẮT BUỘC — BE khai non-nullable, thiếu sẽ lỗi 400
   * chứ không mặc định về hôm nay.
   */
  public async getRemainingInterestAmountByContract(
    contractId: number,
    calculateDate: string
  ): Promise<IContractInterestPayable> {
    const res = await http.get(
      "api/services/app/ContractInquiry/GetRemainingInterestAmountByContract",
      {
        params: { contractId, calculateDate },
      }
    )
    return res.data.result
  }

  /**
   * FB-119 — công nợ hợp đồng ĐANG HIỆU LỰC, trải theo (đợt × loại phí × nhóm NET/VAT).
   * Permission: `PagesAdministration.Contract.PaymentTermRead`.
   *
   * Trả `[]` (KHÔNG throw) khi hợp đồng chưa có sổ công nợ, hoặc khi hợp đồng ĐÃ thanh lý —
   * hợp đồng đã thanh lý phải đọc bằng `getContractRefundSpread`.
   */
  public async getContractDebtSpread(
    params: IContractDebtSpreadParams
  ): Promise<IContractDebtSpreadItem[]> {
    const res = await http.get(
      "api/services/app/ContractInquiry/GetContractDebtSpread",
      { params }
    )
    return res.data.result ?? []
  }

  /**
   * FB-119 — tiền cần hoàn của hợp đồng ĐÃ THANH LÝ, trải theo (đợt × loại phí × nhóm NET/VAT).
   * Permission: `PagesAdministration.Contract.PaymentTermRead`.
   *
   * Trả `[]` (KHÔNG throw) khi hợp đồng chưa thanh lý, hoặc không có phiếu thu nào để hoàn.
   *
   * ⚠️ CỐ Ý không truyền `returnInterestAmount`: bỏ trống = không sinh dòng hoàn lãi `DPI`, khớp
   * đúng hành vi màn lập phiếu hoàn hiện tại. Ngày nào bổ sung thì sửa CẢ HAI trong một change.
   */
  public async getContractRefundSpread(
    params: IContractRefundSpreadParams
  ): Promise<IContractRefundSpreadItem[]> {
    const res = await http.get(
      "api/services/app/ContractInquiry/GetContractRefundSpread",
      { params }
    )
    return res.data.result ?? []
  }

  public async getListContractInquiryWithContract(
    contractId: any
  ): Promise<any> {
    if (!contractId) {
      notifyError(L("ERROR"), L("ENTITY_NOT_FOUND"))
    }
    const res = await http.get(
      "api/services/app/ContractInquiry/GetListContractInquiryWithContract",
      {
        params: { contractId },
      }
    )
    const { result } = res.data
    return result
  }

  public async getListContractEventApprovedWithContract(
    params: any
  ): Promise<any> {
    const res = await http.get(
      "api/services/app/ContractInquiry/GetListContractEventApprovedWithContract",
      {
        params,
      }
    )
    const { result } = res.data
    return result
  }

  public async getAllListReceiptAccounting(params: any): Promise<any> {
    const res = await http.get(
      "api/services/app/ContractInquiry/GetAllContractInquiry",
      {
        params,
      }
    )
    return res.data.result
  }

  public async previewReceiptAccounting(params: any, body: any): Promise<any> {
    const res = await http.post(
      "api/services/app/ContractInquiry/PreviewAccountingIntegrationDetail",
      body,
      { params }
    )
    return res.data.result
  }

  public async previewReceipt(body: any): Promise<any> {
    const res = await http.post(
      "api/services/app/ContractInquiry/PreviewInvoiceIntegrationDetail",
      body
    )
    return res.data.result
  }

  public async getAllAccountingIntegration(params: any): Promise<any> {
    const res = await http.get(
      "api/services/app/ContractInquiry/GetAllAccountingIntegration",
      {
        params,
      }
    )
    return res.data.result
  }

  public async getAllInvoiceIntegration(params: any): Promise<any> {
    const res = await http.get(
      "api/services/app/ContractInquiry/GetAllInvoiceIntegration",
      {
        params,
      }
    )
    return res.data.result
  }

  public async syncInvoiceIntegration(params: any): Promise<any> {
    const res = await http.post(
      "api/services/app/ContractInquiry/CreateInvoiceIntegration",
      params
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SYNC_REQUEST_SUBMITTED")
    )
    return res.data.result
  }

  public async syncAccountingIntegration(params: any, body: any): Promise<any> {
    const res = await http.post(
      "api/services/app/ContractInquiry/CreateAccountingIntegration",
      body,
      { params }
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SYNC_NOTIFY_SUCCESSFULLY")
    )
    return res.data.result
  }

  public async resendSyncAccountingIntegration(params: any): Promise<any> {
    const res = await http.post(
      "api/services/app/ContractInquiry/ResendContractInquiryAccounting",
      params
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SYNC_SUCCESSFULLY")
    )
    return res.data.result
  }

  public async resendSyncReceiptInvoice(params: any): Promise<any> {
    const res = await http.post(
      "api/services/app/ContractInquiry/ResendContractInquiryInvoice",
      params
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SYNC_SUCCESSFULLY")
    )
    return res.data.result
  }

  public async GetPhoebusInvoice(params: any): Promise<any> {
    const res = await http.get(
      "api/services/app/ContractInquiry/GetPhoebusInvoice",
      {
        params,
      }
    )
    return res.data.result
  }

  // Xóa dữ liệu phân bổ phiếu thu để áp dụng lại
  /*
  * params: {
    contractId: 1,
  }
  */
  public async clearContractInquiryToReApply(params: any): Promise<any> {
    const res = await http.post(
      "api/services/app/ContractInquiry/ClearContractInquiryToReApply", null, {
      params
    }
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("UPDATE_SUCCESSFULLY")
    )
    return res.data.result
  }

  // Lấy dữ liệu phân bổ phiếu thu để áp dụng lại
  /*
  * params: {
    contractId: 1,
  }
  */
  public async getAllocationWithContract(params: any): Promise<any> {
    const res = await http.get(
      "api/services/app/ContractInquiry/AllocationWithContract",
      {
        params,
      }
    )
    return res.data.result
  }

  // Phân bổ phiếu thu
  /*
  * body: [
  { 
    contractInquiryId: 238, // ID của bản ghi contract inquiry
    objectType: 136, // Loại đối tượng
    order: 2, // Thứ tự
    feeTypeId: 55, // ID loại phí
    contractId: 92, // ID hợp đồng
    feeGroupId: 1, // ID nhóm phí
    lineType: 2, // Loại dòng
    dueDate: null, // Ngày đến hạn
    calculateDate: "2025-11-12T00:00:00Z", // Ngày tính toán
    actualDate: "2025-11-12T16:59:59.999Z", // Ngày thực tế
    costCenterId: 23, // ID trung tâm chi phí
    debtAmount: 5888629593.00, // Số tiền nợ
    receiptAmount: 0.0, // Số tiền thu
    amount: 505050505.00, // Số tiền
    interestReductionAmount: 0.0, // Số tiền giảm lãi
    phaseCodeId: 56, // ID mã giai đoạn
    lineDescription: null, // Mô tả dòng
    debitAccountId: null, // ID tài khoản nợ
    creditAccountId: null, // ID tài khoản có
    contractPaymentSchedulePeriodId: 1195, // ID kỳ thanh toán theo lịch hợp đồng
    calculateInterest: false // Có tính lãi hay không
  }
  ]
  */
  public async insertContractInquiryDetail(body: any, params: any): Promise<any> {
    const res = await http.post(
      "api/services/app/ContractInquiry/InsertContractInquiryDetail",
      body,
      { params }
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("UPDATE_SUCCESSFULLY")
    )
    return res.data.result
  }

  /**
   * Tải tệp Excel mẫu import phiếu thu (22 cột A→V).
   * Endpoint trả blob và cần token nên phải đi qua http instance rồi `downloadFile`,
   * không dùng `<a href>` trần.
   */
  public async downloadImportReceiptTemplate(): Promise<void> {
    const res = await http.get("api/Imports/GetTemplate", {
      params: { module: ModuleEnum.IMPORT_RECEIPT },
      responseType: "blob",
    })
    downloadFile(
      res.data,
      `IMPORT-TEMPLATE-RECEIPT-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`
    )
  }

  public async validateBeforeImportReceipt(file: File): Promise<any> {
    const formData = new FormData()
    formData.append("file", file)
    const result = await http.post(
      "api/Imports/ContractInquiry/ValidateBeforeImport",
      formData,
      {
        timeout: 60000, // 60s: bulk validation can process tens of thousands of rows
      },
    )
    return result.data.result
  }

  public async importMigrationReceipt(file: File): Promise<any> {
    const formData = new FormData()
    formData.append("file", file)
    const result = await http.post(
      "/api/Imports/Migration/ImportContractInquiry",
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
        },
      }
    )
    return result.data.result
  }
}

export default new ContractInquiryService()
